import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiSave, FiLink, FiBox, FiSmartphone, FiCode } from "react-icons/fi";
import { usePermission } from "../../context/PermissionContext";
import AdsSectionCard, { Spinner } from "./AdsSectionCard";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

/** `/setting/deep-link` call. Answers are HTTP 200 with success:false on failure. */
const request = async (method, path, body) => {
  const token = localStorage.getItem("token");
  const res = await fetch(`${baseUrl}/setting${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false || data.status === false) {
    throw new Error(data?.message || `Request failed with status ${res.status}`);
  }
  return data;
};

/* ─── Field catalog — mirrors DEEP_LINK_FIELDS on the backend ─── */

const FIELDS = [
  { key: "domain", label: "Invite link domain", hint: "Host that serves the .well-known files, e.g. https://pxmine.com. Builds the shareable link /invite/<CODE>.", section: "invite" },
  { key: "scheme", label: "Custom URL scheme", hint: "The app's registered scheme, e.g. pxmine. Enables the 'Open in app' button on the landing page.", section: "invite" },
  { key: "storeUrlAndroid", label: "Google Play link", hint: "Shown on the landing page when the app is not installed.", section: "invite" },
  { key: "storeUrlIos", label: "App Store link", hint: "Shown on the landing page when the app is not installed.", section: "invite" },
  { key: "androidPackageName", label: "Android package name", hint: "Google Play applicationId, e.g. com.pxnetwork.app. Required for Android App Links.", section: "android" },
  { key: "androidSha256Certs", label: "SHA-256 signing fingerprints", hint: "Comma-separated: Play App Signing key + upload key; debug keys for local testing only. assetlinks.json is served only when the package name and at least one fingerprint are set.", section: "android", multiline: true },
  { key: "iosBundleId", label: "iOS bundle ID", hint: "Xcode → Runner → Bundle Identifier. With the Team ID it forms the AASA appID.", section: "ios" },
  { key: "appleTeamId", label: "Apple Team ID", hint: "10 characters, developer.apple.com → Membership. With the bundle ID it forms the AASA appID.", section: "ios" },
  { key: "iosAppStoreId", label: "App Store numeric ID", hint: "Fallback when Team ID + bundle ID are both empty. App Store Connect → App Information.", section: "ios" },
];

const SECTIONS = [
  { key: "invite", icon: FiLink, cls: "bg-blue-50 text-blue-600", title: "Invite link & landing page", description: "The shareable link and the install buttons on the landing page." },
  { key: "android", icon: FiBox, cls: "bg-green-50 text-green-600", title: "Android App Links", description: "Serves /.well-known/assetlinks.json — a 404 until fully set." },
  { key: "ios", icon: FiSmartphone, cls: "bg-purple-50 text-purple-600", title: "iOS Universal Links", description: "Serves /.well-known/apple-app-site-association — a 404 until fully set." },
];

/** Source badge for a field: set here (db), inherited from .env, or unset. */
const SourceBadge = ({ source }) => {
  if (!source || source === "unset") return null;
  const styles = { db: "bg-blue-100 text-blue-700", env: "bg-amber-100 text-amber-700" };
  const labels = { db: "saved here", env: "from .env" };
  return (
    <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${styles[source]}`}>
      {labels[source]}
    </span>
  );
};

/* ─── Live JSON previews, computed the same way the server merges ─── */

const previewAssetlinks = (values) => {
  const shas = String(values.androidSha256Certs || "").split(",").map((s) => s.trim()).filter(Boolean);
  if (!values.androidPackageName || shas.length === 0) return null;
  return [
    {
      relation: ["delegate_permission/common.handle_all_urls"],
      target: { namespace: "android_app", package_name: values.androidPackageName, sha256_cert_fingerprints: shas },
    },
  ];
};

const previewAasa = (values) => {
  const appId =
    values.appleTeamId && values.iosBundleId
      ? `${values.appleTeamId.trim()}.${values.iosBundleId.trim()}`
      : values.iosAppStoreId
        ? String(values.iosAppStoreId).trim()
        : "";
  if (!appId) return null;
  return { applinks: { details: [{ appIDs: [appId], components: [{ "/": "/invite/*" }] }] } };
};

const previewInviteUrl = (values) =>
  values.domain ? `${String(values.domain).trim().replace(/\/+$/, "")}/invite/<CODE>` : null;

const JsonPreview = ({ label, doc, emptyHint }) => (
  <div>
    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-1 flex items-center gap-1">
      <FiCode size={11} /> {label}
    </p>
    {doc ? (
      <pre className="text-xs bg-gray-50 border border-gray-100 rounded-lg p-3 overflow-x-auto text-gray-700">{JSON.stringify(doc, null, 2)}</pre>
    ) : (
      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg p-3">{emptyHint}</p>
    )}
  </div>
);

/* ─── One editable field ─── */

const TextField = ({ field, value, onChange, source, multiline }) => (
  <div>
    <label className="flex items-center gap-2 text-xs font-medium text-gray-600 mb-1">
      {field.label} <SourceBadge source={source} />
    </label>
    {multiline ? (
      <textarea
        rows={2}
        value={value}
        onChange={onChange}
        placeholder={field.hint}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-300 resize-y"
      />
    ) : (
      <input
        value={value}
        onChange={onChange}
        placeholder={field.hint}
        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
      />
    )}
    <p className="text-[11px] text-gray-400 mt-1">{field.hint}</p>
  </div>
);

/* ─── Main component ─── */

const EMPTY_FORM = Object.fromEntries(FIELDS.map((f) => [f.key, ""]));

const DeepLinkSettings = () => {
  const { hasPermission } = usePermission();
  const canView = hasPermission("setting", "view");
  const canEdit = hasPermission("setting", "edit");

  const [form, setForm] = useState(EMPTY_FORM);
  const [envValues, setEnvValues] = useState(EMPTY_FORM); // what .env currently provides
  const [sources, setSources] = useState({}); // field -> "db" | "env" | "unset"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await request("GET", "/deep-link");
      const fields = result.data?.fields || [];
      const next = { ...EMPTY_FORM };
      const nextEnv = { ...EMPTY_FORM };
      const nextSources = {};
      for (const f of fields) {
        nextSources[f.field] = f.source;
        if (f.source === "db") next[f.field] = f.value;
        else if (f.source === "env") {
          next[f.field] = f.value;
          nextEnv[f.field] = f.value; // remember it so a cleared field falls back
        }
      }
      setForm(next);
      setEnvValues(nextEnv);
      setSources(nextSources);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to load deep link settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  /** What the server will serve after this save: a typed value, else the .env value. */
  const effective = {};
  for (const f of FIELDS) {
    effective[f.key] = form[f.key].trim() || envValues[f.key];
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await request("PUT", "/deep-link", form);
      toast.success("Deep link settings saved — live on this server now");
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to save deep link settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5 text-sm">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-500 max-w-3xl">
        These values power the referral invite link (<code className="font-mono">https://&lt;domain&gt;/invite/&lt;code&gt;</code>),
        the landing-page buttons, and the Android/iOS <code className="font-mono">.well-known</code> files.
        Leave a field empty to keep the server&apos;s <code className="font-mono">.env</code> value.
        While a platform&apos;s fields are incomplete its well-known file stays a 404 — a wrong-but-served
        file makes the OS treat every link as &ldquo;verified, do not open&rdquo;.
      </p>

      {SECTIONS.map((section) => {
        const Icon = section.icon;
        const sectionFields = FIELDS.filter((f) => f.section === section.key);
        return (
          <AdsSectionCard key={section.key} icon={Icon} iconClass={section.cls} title={section.title} description={section.description}>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sectionFields.map((f) => (
                  <TextField
                    key={f.key}
                    field={f}
                    source={sources[f.key]}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    multiline={f.multiline}
                  />
                ))}
              </div>

              {section.key === "invite" && (
                <JsonPreview
                  label="Shareable link (sample)"
                  doc={previewInviteUrl(effective) ? { link: previewInviteUrl(effective) } : null}
                  emptyHint="Domain not set — the app shares the bare referral code until this is filled in."
                />
              )}
              {section.key === "android" && (
                <JsonPreview
                  label="assetlinks.json (what the OS fetches)"
                  doc={previewAssetlinks(effective)}
                  emptyHint="assetlinks.json is a 404 until the package name and at least one SHA-256 fingerprint are set."
                />
              )}
              {section.key === "ios" && (
                <JsonPreview
                  label="apple-app-site-association (what Apple fetches)"
                  doc={previewAasa(effective)}
                  emptyHint="The AASA file is a 404 until Team ID + bundle ID (or the App Store ID) are set."
                />
              )}
            </div>
          </AdsSectionCard>
        );
      })}

      {canEdit ? (
        <div className="flex items-center justify-between gap-3 bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
          <p className="text-xs text-gray-500">Saving applies all fields; empty fields keep the .env fallback.</p>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition"
          >
            <FiSave size={14} />
            {saving ? "Saving..." : "Save settings"}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
          <p className="text-xs text-gray-400">You need edit permission on &ldquo;setting&rdquo; to change these values.</p>
        </div>
      )}
    </div>
  );
};

export default DeepLinkSettings;
